import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, Lock, Plus } from "lucide-react";
import SpinWheel from "../components/bond-wheel/SpinWheel";
import Countdown from "../components/bond-wheel/Countdown";
import CreateBondWheelModal from "../components/bond-wheel/CreateBondWheelModal";
import CreateBondSpinModal from "../components/bond-spin/CreateBondSpinModal";
import PlaceBondSpinBetModal from "../components/bond-spin/PlaceBondSpinBetModal";
import BondWheelJoinModal from "../components/bond-wheel/BondWheelJoinModal";
import BondWheelLeaveModal from "../components/bond-wheel/BondWheelLeaveModal";
import BondWheelGameRail from "../components/bond-play/BondWheelGameRail";
import BondSpinGameRail from "../components/bond-play/BondSpinGameRail";
import BondSpinStageActions from "../components/bond-play/BondSpinStageActions";
import BondSpinStagePanel from "../components/bond-play/BondSpinStagePanel";
import BondSpinTablePnl, { BondSpinPnlStamp } from "../components/bond-spin/BondSpinTablePnl";
import BondPlayStageHelpButton from "../components/bond-play/BondPlayStageHelpButton";
import BondSpinInfoModal from "../components/bond-play/BondSpinInfoModal";
import BondWheelInfoModal from "../components/bond-play/BondWheelInfoModal";
import BondPlayConfirmModal from "../components/bond-play/BondPlayConfirmModal";
import BondSpinResultModal from "../components/bond-play/BondSpinResultModal";
import RouletteWheel from "../components/bond-spin/RouletteWheel";
import BondDrawNumberGrid from "../components/bond-draw/BondDrawNumberGrid";
import BondDrawTicket from "../components/bond-draw/BondDrawTicket";
import BondDrawStageActions from "../components/bond-draw/BondDrawStageActions";
import BondDrawSidePanel from "../components/bond-draw/BondDrawSidePanel";
import BondSpinPendingExpiryHint from "../components/bond-play/BondSpinPendingExpiryHint";
import BondDrawPendingExpiryHint from "../components/bond-draw/BondDrawPendingExpiryHint";
import BondDrawStageHeader from "../components/bond-draw/BondDrawStageHeader";
import BondDrawInfoModal from "../components/bond-draw/BondDrawInfoModal";
import BondDrawResultModal from "../components/bond-draw/BondDrawResultModal";
import BondDrawInvestModal from "../components/bond-draw/BondDrawInvestModal";
import BondPlayGameTypeRail from "../components/bond-play/BondPlayGameTypeRail";
import { BOND_PLAY_GAME_TYPES, findBondPlayGameType } from "../utils/bondPlayGameTypes";
import BondWheelStagePlayers from "../components/bond-play/BondWheelStagePlayers";
import BondWheelStageResult from "../components/bond-play/BondWheelStageResult";
import { useBondWheelLiveSpin } from "../hooks/useBondWheelLiveSpin";
import { useBondSpinPlay } from "../hooks/useBondSpinPlay";
import { useBondDrawPlay } from "../hooks/useBondDrawPlay";
import { useBondDrawPendingExpiry } from "../hooks/useBondDrawPendingExpiry";
import { useCasinoPendingExpiry } from "../hooks/useCasinoPendingExpiry";
import { selectAppIsAuthenticated, selectAppUser } from "../redux/appAuthSlice";
import {
  bondWheelCreate,
  bondWheelJoin,
  bondWheelLeave,
  fetchBondWheelConstants,
  fetchBondWheelGame,
  fetchBondWheelGames,
  selectBondWheelActionLoading,
  selectBondWheelConstants,
  selectBondWheelGameById,
  selectBondWheelGames,
  selectBondWheelLoading,
  selectBondWheelParticipation,
} from "../redux/bondWheelSlice";
import { initBondWheelSocket } from "../redux/bondWheelSocket";
import {
  casinoCreate,
  casinoCancel,
  casinoJoin,
  fetchCasinoGames,
  fetchCasinoPlayers,
  fetchUserPendingCasinoBets,
  removePendingBet,
  selectCasinoActionLoading,
  selectCasinoGameById,
  selectCasinoGames,
  selectCasinoLoading,
  selectPendingCasinoBets,
} from "../redux/casinoSlice";
import { initCasinoSocket } from "../redux/casinoSocket";
import {
  bondDrawBuyRound,
  bondDrawInvest,
  clearPendingRound,
  fetchBondDrawInvestors,
  fetchBondDrawLanding,
  fetchBondDrawPool,
  selectBondDrawActionLoading,
  selectBondDrawInvestorActionLoading,
  selectBondDrawInvestors,
  selectBondDrawPendingRound,
  selectBondDrawLoading,
  selectBondDrawPool,
} from "../redux/bondDrawSlice";
import {
  buildTicketQuote,
  buildTicketQuoteFromRound,
  formatDrawCoin,
  MAX_SELECTION,
} from "../utils/bondDrawHelpers";
import {
  canJoinOrLeaveGame,
  formatCoin,
  getGameWheelEntries,
  getUserTicketCount,
  getSettledWheelRotation,
  makeJoinIdempotencyKey,
} from "../utils/bondWheelHelpers";
import { formatCasinoCoin, isCasinoCreator, LIVE_CASINO_STATUSES, sortCasinoGamesForRail, formatCasinoOutcomeLabel, getPendingBetGameIds, getCasinoBetId, getPlayablePendingRouletteBets, getAllPendingRouletteBets, getCasinoActionErrorMessage, getCasinoTablePnl } from "../utils/casinoHelpers";
import { isPendingCasinoBetPlayable } from "../utils/casinoPendingExpiry";
import { store } from "../../redux/store";
import "../bond-play.css";
import "../bond-wheel.css";
import "../bond-spin.css";
import "../bond-spin-bet.css";
import "../bond-draw.css";

const WHEEL_SIZE = 300;
const SPIN_WHEEL_SIZE = 300;
const LIVE_GAME_STATUSES = ["Active", "Locked"];
const TERMINAL_GAME_STATUSES = ["Settled", "Cancelled"];

const BondPlay = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectAppIsAuthenticated);
  const user = useSelector(selectAppUser);
  const userId = user?._id;
  const userCoin = Number(user?.COIN || 0);
  const wheelGames = useSelector(selectBondWheelGames);
  const casinoGames = useSelector(selectCasinoGames);
  const loading = useSelector(selectBondWheelLoading);
  const spinLoading = useSelector(selectCasinoLoading);
  const spinActionLoading = useSelector(selectCasinoActionLoading);
  const actionLoading = useSelector(selectBondWheelActionLoading);
  const constants = useSelector(selectBondWheelConstants);

  const queryGameId = searchParams.get("game");
  const initialGameId = BOND_PLAY_GAME_TYPES.some((g) => g.id === queryGameId)
    ? queryGameId
    : BOND_PLAY_GAME_TYPES[0].id;
  const [gameTypeId, setGameTypeId] = useState(initialGameId);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [selectedSpinGameId, setSelectedSpinGameId] = useState(null);
  const [selectedSpinBetId, setSelectedSpinBetId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSpinOpen, setCreateSpinOpen] = useState(false);
  const [placeBetOpen, setPlaceBetOpen] = useState(false);
  const [spinConfirm, setSpinConfirm] = useState(null);
  const [joinConfirmGame, setJoinConfirmGame] = useState(null);
  const [leaveConfirmGame, setLeaveConfirmGame] = useState(null);
  const [busyGameId, setBusyGameId] = useState(null);
  const [leavingGameId, setLeavingGameId] = useState(null);
  const [toast, setToast] = useState(null);

  const gameType = useMemo(() => findBondPlayGameType(gameTypeId), [gameTypeId]);

  useEffect(() => {
    if (!queryGameId || !BOND_PLAY_GAME_TYPES.some((g) => g.id === queryGameId)) return;
    setGameTypeId(queryGameId);
  }, [queryGameId]);

  const railGames = useMemo(() => {
    const list = (wheelGames || []).filter((g) =>
      [...LIVE_GAME_STATUSES, ...TERMINAL_GAME_STATUSES].includes(g.status)
    );
    list.sort((a, b) => {
      const aLive = LIVE_GAME_STATUSES.includes(a.status) ? 0 : 1;
      const bLive = LIVE_GAME_STATUSES.includes(b.status) ? 0 : 1;
      if (aLive !== bLive) return aLive - bLive;
      if (a.status === "Settled" && b.status === "Settled") {
        return new Date(b.settledAt || b.deadline) - new Date(a.settledAt || a.deadline);
      }
      return new Date(a.deadline) - new Date(b.deadline);
    });
    return list;
  }, [wheelGames]);

  const selectedGame = useSelector((state) => selectBondWheelGameById(state, selectedGameId));
  const selectedSpinGame = useSelector((state) => selectCasinoGameById(state, selectedSpinGameId));
  const pendingCasinoBets = useSelector(selectPendingCasinoBets);
  const playablePendingCasinoBets = useMemo(
    () => getPlayablePendingRouletteBets(pendingCasinoBets),
    [pendingCasinoBets]
  );
  const pendingBetGameIds = useMemo(
    () => getPendingBetGameIds(playablePendingCasinoBets),
    [playablePendingCasinoBets]
  );
  const activeSpinPendingBet = useMemo(
    () => playablePendingCasinoBets[0] || null,
    [playablePendingCasinoBets]
  );
  const selectedSpinBet = useMemo(() => {
    if (!selectedSpinBetId) return null;
    return pendingCasinoBets.find((bet) => getCasinoBetId(bet) === String(selectedSpinBetId)) || null;
  }, [pendingCasinoBets, selectedSpinBetId]);
  const timerSpinBet = activeSpinPendingBet || selectedSpinBet;
  const spinPendingExpiry = useCasinoPendingExpiry(timerSpinBet);
  const hasAnyPendingRouletteBet = useMemo(
    () => getAllPendingRouletteBets(pendingCasinoBets).length > 0,
    [pendingCasinoBets]
  );
  const hasPlayablePendingBet = playablePendingCasinoBets.length > 0;
  const showPlayTimer =
    hasPlayablePendingBet &&
    spinPendingExpiry.secondsLeft != null &&
    spinPendingExpiry.secondsLeft > 0;
  const spinExpiredHandledRef = useRef(null);

  const spinRailGames = useMemo(() => sortCasinoGamesForRail(casinoGames), [casinoGames]);

  const participation = useSelector((state) =>
    selectBondWheelParticipation(state, selectedGame?._id, userId)
  );

  const { isWheelSpinning, winningSliceIndex, winningSliceIndices } = useBondWheelLiveSpin(selectedGame, selectedGameId);

  const {
    isSpinPlaying,
    wheelRotationDeg,
    wheelSettleMs,
    spinGameResult,
    spinResultModal,
    playSpinBet,
    closeSpinResultModal,
  } = useBondSpinPlay(dispatch, userId);

  const drawPendingRound = useSelector(selectBondDrawPendingRound);
  const drawPool = useSelector(selectBondDrawPool);
  const drawInvestors = useSelector(selectBondDrawInvestors);
  const drawLoading = useSelector(selectBondDrawLoading);
  const drawActionLoading = useSelector(selectBondDrawActionLoading);
  const drawInvestorActionLoading = useSelector(selectBondDrawInvestorActionLoading);
  const pendingExpiry = useBondDrawPendingExpiry(drawPendingRound);
  const drawExpiredHandledRef = useRef(null);
  const [drawSelectedNumbers, setDrawSelectedNumbers] = useState([]);
  const [drawInvestOpen, setDrawInvestOpen] = useState(false);
  const [drawConfirm, setDrawConfirm] = useState(null);
  const [drawReserveQuote, setDrawReserveQuote] = useState(null);
  const [drawStatusMessage, setDrawStatusMessage] = useState(null);
  const [drawInfoOpen, setDrawInfoOpen] = useState(false);
  const [spinInfoOpen, setSpinInfoOpen] = useState(false);
  const [wheelInfoOpen, setWheelInfoOpen] = useState(false);

  const {
    isPlaying: isDrawPlaying,
    drawPhase,
    drawnNumbers,
    revealedDrawnCount,
    resultModal: drawResultModal,
    playRound: playDrawRound,
    cancelRound: cancelDrawRound,
    closeResultModal: closeDrawResultModal,
  } = useBondDrawPlay(dispatch);

  useEffect(() => {
    dispatch(fetchBondWheelGames({ limit: 60 }));
    dispatch(fetchBondWheelGames({ status: "Settled", limit: 100 }));
    dispatch(fetchCasinoGames({ limit: 40 }));
    if (!constants) dispatch(fetchBondWheelConstants());
  }, [dispatch, constants]);

  useEffect(() => {
    initBondWheelSocket(dispatch, store.getState);
    initCasinoSocket(dispatch, store.getState);
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    dispatch(fetchUserPendingCasinoBets(userId));
  }, [dispatch, isAuthenticated, userId]);

  useEffect(() => {
    if (gameTypeId !== "bond-spin") return;
    dispatch(fetchCasinoPlayers());
    if (isAuthenticated && userId) {
      dispatch(fetchUserPendingCasinoBets(userId));
    }
  }, [dispatch, gameTypeId, isAuthenticated, userId]);

  useEffect(() => {
    if (gameTypeId !== "bond-draw") return;
    dispatch(fetchBondDrawPool());
    dispatch(fetchBondDrawInvestors({ limit: 100 }));
    if (!isAuthenticated || !userId) return;
    dispatch(fetchBondDrawLanding());
  }, [dispatch, gameTypeId, isAuthenticated, userId]);

  const isDrawInvestor = useMemo(() => {
    if (!userId || !Array.isArray(drawInvestors)) return false;
    return drawInvestors.some((inv) => String(inv.userId) === String(userId));
  }, [drawInvestors, userId]);

  useEffect(() => {
    if (gameTypeId !== "bond-draw") return;
    if (drawPendingRound?.selectedNumbers?.length) {
      setDrawSelectedNumbers(drawPendingRound.selectedNumbers);
    } else if (!drawPendingRound && !isDrawPlaying && !drawResultModal) {
      setDrawSelectedNumbers([]);
    }
  }, [gameTypeId, drawPendingRound, isDrawPlaying, drawResultModal]);

  useEffect(() => {
    if (gameTypeId !== "bond-draw" || !drawResultModal) return;
    setDrawSelectedNumbers([]);
  }, [gameTypeId, drawResultModal]);

  useEffect(() => {
    const hasOverdueLive = (wheelGames || []).some((game) => {
      if (!LIVE_GAME_STATUSES.includes(game.status)) return false;
      const deadlineMs = new Date(game.deadline).getTime();
      return Number.isFinite(deadlineMs) && deadlineMs <= Date.now();
    });
    if (!hasOverdueLive) return undefined;

    dispatch(fetchBondWheelGames({ limit: 60 }));
    dispatch(fetchBondWheelGames({ status: "Settled", limit: 100 }));
    const interval = setInterval(() => {
      dispatch(fetchBondWheelGames({ limit: 60 }));
      dispatch(fetchBondWheelGames({ status: "Settled", limit: 100 }));
    }, 5000);
    return () => clearInterval(interval);
  }, [dispatch, wheelGames]);

  useEffect(() => {
    if (gameTypeId !== "bond-spin") return;
    if (selectedSpinGameId && spinRailGames.some((g) => String(g._id) === String(selectedSpinGameId))) return;
    const firstLive = spinRailGames.find((g) => LIVE_CASINO_STATUSES.includes(g.status));
    setSelectedSpinGameId(
      firstLive?._id ? String(firstLive._id) : spinRailGames[0]?._id ? String(spinRailGames[0]._id) : null
    );
  }, [gameTypeId, spinRailGames, selectedSpinGameId]);

  useEffect(() => {
    if (gameTypeId !== "bond-spin" || !selectedSpinGame?.deadline) return undefined;
    const deadlineMs = new Date(selectedSpinGame.deadline).getTime();
    if (!Number.isFinite(deadlineMs)) return undefined;

    const refreshGames = () => dispatch(fetchCasinoGames({ limit: 40 }));
    const delay = deadlineMs - Date.now();

    if (delay <= 0) {
      refreshGames();
      return undefined;
    }

    const timer = window.setTimeout(refreshGames, delay + 500);
    return () => window.clearTimeout(timer);
  }, [dispatch, gameTypeId, selectedSpinGame?._id, selectedSpinGame?.deadline]);

  useEffect(() => {
    if (gameTypeId !== "bond-wheel") return;
    if (selectedGameId && railGames.some((g) => String(g._id) === String(selectedGameId))) return;
    const firstLive = railGames.find((g) => LIVE_GAME_STATUSES.includes(g.status));
    setSelectedGameId(
      firstLive?._id ? String(firstLive._id) : railGames[0]?._id ? String(railGames[0]._id) : null
    );
  }, [gameTypeId, railGames, selectedGameId]);

  useEffect(() => {
    if (!selectedGameId) return;
    dispatch(fetchBondWheelGame({ gameId: selectedGameId }));
  }, [dispatch, selectedGameId]);

  useEffect(() => {
    setSelectedSpinBetId((prev) => {
      const allPending = getPlayablePendingRouletteBets(pendingCasinoBets);
      if (prev && allPending.some((bet) => getCasinoBetId(bet) === String(prev))) {
        return prev;
      }
      const first = allPending[0];
      return first ? getCasinoBetId(first) : null;
    });
  }, [pendingCasinoBets]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!drawStatusMessage) return undefined;
    const timer = setTimeout(() => setDrawStatusMessage(null), 2800);
    return () => clearTimeout(timer);
  }, [drawStatusMessage]);

  const wheelParticipants = useMemo(() => {
    if (!selectedGame) return [];
    return getGameWheelEntries(selectedGame).map((entry) => ({
      id: entry._id,
      name: entry.user?.name || "Player",
      user: entry.user,
    }));
  }, [selectedGame]);

  const isTerminalGame = selectedGame && TERMINAL_GAME_STATUSES.includes(selectedGame.status);
  const isSettledGame = selectedGame?.status === "Settled";
  const isLiveGame = selectedGame && LIVE_GAME_STATUSES.includes(selectedGame.status);

  const settledRotationDeg = useMemo(() => {
    if (!isSettledGame || !Number.isFinite(selectedGame?.winningSliceIndex)) return null;
    return getSettledWheelRotation(selectedGame.winningSliceIndex, wheelParticipants.length);
  }, [selectedGame?.winningSliceIndex, isSettledGame, wheelParticipants.length]);

  const handleSelectGame = useCallback((game) => {
    if (!game?._id) return;
    setSelectedGameId(String(game._id));
  }, []);

  const handleSelectSpinGame = useCallback((game) => {
    if (!game?._id) return;
    setSelectedSpinGameId(String(game._id));
  }, []);

  const handleCreateSpin = async (payload) => {
    if (!isAuthenticated) {
      navigate("/app/login");
      throw new Error("Sign in required");
    }
    const res = await dispatch(casinoCreate({ ...payload, creatorId: userId })).unwrap();
    if (!res?.casinoGame?._id) {
      throw new Error("Server did not return a valid table. Please try again.");
    }
    setSelectedSpinGameId(String(res.casinoGame._id));
    setToast("Spin table created.");
    return res;
  };

  const openCreateSpin = () => {
    if (!isAuthenticated) {
      navigate("/app/login");
      return;
    }
    setCreateSpinOpen(true);
  };

  const handleOpenPlaceBet = () => {
    if (!selectedSpinGame?._id) return;
    if (!isAuthenticated) {
      navigate("/app/login");
      return;
    }
    if (isCasinoCreator(selectedSpinGame, userId)) {
      setToast("You can't bet on your own table.");
      return;
    }
    if (hasAnyPendingRouletteBet) {
      setToast(
        hasPlayablePendingBet
          ? "You already have a pending bet. Play, cancel, or wait for it to expire before placing another."
          : "Your previous bet expired and is being refunded. Please try again shortly."
      );
      return;
    }
    setPlaceBetOpen(true);
  };

  const handleSpinPendingExpired = useCallback(() => {
    if (activeSpinPendingBet?._id) {
      dispatch(removePendingBet(activeSpinPendingBet._id));
    }
    if (userId) {
      dispatch(fetchUserPendingCasinoBets(userId));
      dispatch(fetchCasinoPlayers());
    }
    setSelectedSpinBetId(null);
    setToast("Bet expired. Stake refunded.");
  }, [dispatch, activeSpinPendingBet?._id, userId]);

  useEffect(() => {
    spinExpiredHandledRef.current = null;
  }, [timerSpinBet?._id]);

  useEffect(() => {
    if (!timerSpinBet?._id || !spinPendingExpiry.isExpired) return;
    if (spinExpiredHandledRef.current === timerSpinBet._id) return;
    spinExpiredHandledRef.current = timerSpinBet._id;
    handleSpinPendingExpired();
  }, [timerSpinBet, spinPendingExpiry.isExpired, handleSpinPendingExpired]);

  const handleCancelSpinBet = () => {
    if (!selectedSpinBet?._id || !userId) {
      setToast("Select a bet in My games to cancel.");
      return;
    }
    if (isSpinPlaying) return;
    setSpinConfirm("cancel-bet");
  };

  const handlePlaySpinBet = async () => {
    if (!selectedSpinBet?._id || !userId || isSpinPlaying || spinPendingExpiry.isExpired) return;
    try {
      await playSpinBet(selectedSpinBet);
    } catch (error) {
      setToast(typeof error === "string" ? error : error?.message || "Could not resolve spin.");
    }
  };

  const handleCloseSpinResult = () => {
    const playedBetId = closeSpinResultModal();
    if (playedBetId && String(selectedSpinBetId) === String(playedBetId)) {
      const remaining = getPlayablePendingRouletteBets(pendingCasinoBets).filter(
        (bet) => getCasinoBetId(bet) !== String(playedBetId)
      );
      setSelectedSpinBetId(remaining[0] ? getCasinoBetId(remaining[0]) : null);
    }
    if (userId) {
      dispatch(fetchUserPendingCasinoBets(userId));
      dispatch(fetchCasinoPlayers());
    }
  };

  const handleSpinConfirm = async () => {
    if (!spinConfirm || !userId) return;

    try {
      if (spinConfirm === "cancel-bet") {
        if (!selectedSpinBet?._id) return;
        await dispatch(
          casinoCancel({
            betId: selectedSpinBet._id,
            bettorId: userId,
          })
        ).unwrap();
        setToast("Bet cancelled. Funds returned.");
      }

      dispatch(fetchUserPendingCasinoBets(userId));
      setSpinConfirm(null);
    } catch (error) {
      setToast(typeof error === "string" ? error : error?.message || "Something went wrong.");
    }
  };

  const spinConfirmModal = useMemo(() => {
    if (spinConfirm === "cancel-bet" && selectedSpinBet) {
      return {
        eyebrow: "Cancel bet",
        title: "Cancel this bet?",
        tone: "danger",
        confirmLabel: "Cancel bet",
        cancelLabel: "Keep bet",
        loadingLabel: "Cancelling…",
        description: (
          <>
            <p>
              Your <strong>{formatCasinoCoin(selectedSpinBet.betAmount)} COIN</strong> stake will be
              returned to your wallet.
            </p>
            {selectedSpinBet.selectedOutcome ? (
              <p className="bplay-confirm-muted">
                Outcome: {formatCasinoOutcomeLabel(selectedSpinBet.selectedOutcome)}
              </p>
            ) : null}
          </>
        ),
      };
    }

    return null;
  }, [spinConfirm, selectedSpinBet]);

  const handlePlaceBet = async ({ selectedOutcome, betAmount }) => {
    if (!selectedSpinGame?._id) {
      throw new Error("No table selected.");
    }
    if (hasAnyPendingRouletteBet) {
      throw new Error(
        hasPlayablePendingBet
          ? "You already have a pending bet. Play, cancel, or wait for it to expire before placing another."
          : "Your previous bet expired and is being refunded. Please try again shortly."
      );
    }

    try {
      const res = await dispatch(
        casinoJoin({
          gameId: selectedSpinGame._id,
          bettorId: userId,
          selectedOutcome,
          betAmount,
        })
      ).unwrap();

      if (!res?.bet?._id) {
        throw new Error("Server did not confirm your bet. Please try again.");
      }

      setToast("Bet placed.");
      if (res?.bet?._id) setSelectedSpinBetId(String(res.bet._id));
      dispatch(fetchUserPendingCasinoBets(userId));
      return res;
    } catch (e) {
      throw new Error(getCasinoActionErrorMessage(e, "Could not reverse ticket."));
    }
  };

  const handleCreate = async ({ entryAmount, deadline, numberOfWinners, minimumPlayers }) => {
    if (!isAuthenticated) {
      navigate("/app/login");
      throw new Error("Sign in required");
    }
    const createIdempotencyKey = `bw-create-${userId}-${Date.now()}`;
    const res = await dispatch(
      bondWheelCreate({ entryAmount, deadline, numberOfWinners, minimumPlayers, createIdempotencyKey })
    ).unwrap();

    if (!res?.game?._id) {
      throw new Error("Server did not return a valid game. Please try again.");
    }

    setSelectedGameId(String(res.game._id));
    setToast(res?.reused ? "Your wheel is ready." : "Wheel created.");
    return res;
  };

  const executeJoin = async (game, ticketCount = 1) => {
    if (!game?._id) return;
    setBusyGameId(game._id);
    try {
      await dispatch(
        bondWheelJoin({
          gameId: game._id,
          ticketCount,
          joinIdempotencyKey: makeJoinIdempotencyKey(userId, game._id),
        })
      ).unwrap();
      setJoinConfirmGame(null);
      setSelectedGameId(String(game._id));
      setToast(ticketCount > 1 ? `${ticketCount} tickets added.` : "Ticket added.");
    } catch (e) {
      setToast(e?.message || "Could not join.");
    } finally {
      setBusyGameId(null);
    }
  };

  const executeLeave = async (game) => {
    if (!game?._id) return;
    setLeavingGameId(game._id);
    try {
      const res = await dispatch(bondWheelLeave({ gameId: game._id })).unwrap();
      setLeaveConfirmGame(null);
      setToast(`${formatCoin(res?.refundAmount || game.entryAmount)} COIN refunded.`);
      if (String(selectedGameId) === String(game._id)) {
        const remaining = railGames.filter((g) => String(g._id) !== String(game._id));
        const nextLive = remaining.find((g) => LIVE_GAME_STATUSES.includes(g.status));
        setSelectedGameId(nextLive?._id ? String(nextLive._id) : remaining[0]?._id ? String(remaining[0]._id) : null);
      }
    } catch (e) {
      setToast(e?.message || "Could not leave.");
    } finally {
      setLeavingGameId(null);
    }
  };

  const joinWindowOpen = useMemo(
    () => (selectedGame ? canJoinOrLeaveGame(selectedGame, Date.now(), constants) : false),
    [selectedGame, constants]
  );

  const showJoinAction = useMemo(() => {
    if (!isLiveGame) return false;
    return participation.canJoin || joinWindowOpen;
  }, [isLiveGame, participation, joinWindowOpen]);

  const handleJoinAction = () => {
    if (!selectedGame) return;
    if (!isAuthenticated) {
      navigate("/app/login");
      return;
    }
    setJoinConfirmGame(selectedGame);
  };

  const renderSpinCenter = () => {
    if (spinLoading && spinRailGames.length === 0) {
      return (
        <div className="bplay-wheel-loading">
          <Loader2 size={28} className="bplay-spin" />
        </div>
      );
    }

    if (!selectedSpinGame) {
      return (
        <div className="bplay-wheel-empty bplay-spin-empty-with-panel">
          <BondPlayStageHelpButton
            helpLabel="How Bond Spin works"
            onOpenInfo={() => setSpinInfoOpen(true)}
          />
          <div className="bplay-spin-stage">
            <RouletteWheel size={SPIN_WHEEL_SIZE} accent={gameType.accent} />
          </div>
          <button type="button" className="bplay-spin-create" onClick={openCreateSpin}>
            <Plus size={18} />
            <span>Create spin</span>
          </button>
          <BondSpinStagePanel />
        </div>
      );
    }

    const isLive = LIVE_CASINO_STATUSES.includes(selectedSpinGame.status);
    const hasActiveSelectedBet =
      !!selectedSpinBet && isPendingCasinoBetPlayable(selectedSpinBet);
    const showPlayerActions = isLive && !isCasinoCreator(selectedSpinGame, userId);
    const tablePnl = getCasinoTablePnl(selectedSpinGame);
    const isMineTable = isCasinoCreator(selectedSpinGame, userId);

    return (
      <div className="bplay-spin-stage-shell">
        <BondPlayStageHelpButton
          helpLabel="How Bond Spin works"
          onOpenInfo={() => setSpinInfoOpen(true)}
        />
        <div className={`bplay-spin-stage${isSpinPlaying ? " is-spinning" : ""}`}>
          <RouletteWheel
            size={SPIN_WHEEL_SIZE}
            accent={gameType.accent}
            idleSpin={!isSpinPlaying && wheelRotationDeg == null}
            rotationDeg={wheelRotationDeg}
            resultPocket={spinGameResult}
            settleMs={wheelSettleMs}
          />
        </div>

        <div className="bplay-spin-meta">
          {showPlayTimer ? (
            <BondSpinPendingExpiryHint
              deadline={spinPendingExpiry.deadline}
              secondsLeft={spinPendingExpiry.secondsLeft}
            />
          ) : isLive ? (
            <Countdown deadline={selectedSpinGame.deadline} size="hero" tone="live" />
          ) : (
            <span className="bplay-spin-meta-sub">Finished</span>
          )}
          {tablePnl.available ? (
            <div className="bplay-spin-pnl">
              <div className="bplay-spin-pnl-head">
                <span className="bplay-spin-pnl-pool">
                  {formatCasinoCoin(selectedSpinGame.allocatedAmount)} COIN
                </span>
                <BondSpinPnlStamp pnl={tablePnl} />
              </div>
              <BondSpinTablePnl pnl={tablePnl} isMine={isMineTable} />
            </div>
          ) : null}
        </div>

        {showPlayerActions ? (
          <BondSpinStageActions
            hasSelectedBet={hasActiveSelectedBet}
            canPlaceBet={!hasAnyPendingRouletteBet}
            disabled={spinActionLoading || isSpinPlaying}
            onCancel={handleCancelSpinBet}
            onPlay={handlePlaySpinBet}
            onPlaceBet={handleOpenPlaceBet}
          />
        ) : null}

        <BondSpinStagePanel />
      </div>
    );
  };

  const renderWheelCenter = () => {
    if (loading && railGames.length === 0) {
      return (
        <div className="bplay-wheel-loading">
          <Loader2 size={28} className="bplay-spin" />
        </div>
      );
    }

    if (!selectedGame) {
      return (
        <div className="bplay-wheel-stage-shell">
          <BondPlayStageHelpButton
            helpLabel="How Bond Wheel works"
            onOpenInfo={() => setWheelInfoOpen(true)}
          />
          <div className="bplay-wheel-empty">
            <div className="bplay-wheel-wrap bplay-wheel-wrap-idle">
              <SpinWheel participants={[]} size={WHEEL_SIZE} />
            </div>
            <button
              type="button"
              className="bplay-wheel-create"
              onClick={() => (isAuthenticated ? setCreateOpen(true) : navigate("/app/login"))}
            >
              <Plus size={18} />
              <span>Create wheel</span>
            </button>
          </div>
        </div>
      );
    }

    const isLocked = selectedGame.status === "Locked";
    const sliceIndex = Number.isFinite(winningSliceIndex)
      ? winningSliceIndex
      : Number.isFinite(selectedGame.winningSliceIndex)
        ? selectedGame.winningSliceIndex
        : null;

    return (
      <div className="bplay-wheel-stage-shell">
        <BondPlayStageHelpButton
          helpLabel="How Bond Wheel works"
          onOpenInfo={() => setWheelInfoOpen(true)}
        />
        <div className="bplay-wheel-wrap">
          <SpinWheel
            participants={wheelParticipants}
            size={WHEEL_SIZE}
            winningSliceIndex={sliceIndex}
            winningSliceIndices={winningSliceIndices}
            highlightUserId={userId}
            spinning={isWheelSpinning}
            settledRotationDeg={settledRotationDeg}
            settleInstant={isSettledGame && !isWheelSpinning}
          />
        </div>

        <div className="bplay-wheel-status">
          {isTerminalGame ? null : (
            <Countdown
              deadline={selectedGame.deadline}
              size="hero"
              tone={isLocked ? "lock" : "live"}
              showSpinning={isWheelSpinning}
            />
          )}
        </div>

        {isTerminalGame ? (
          <BondWheelStageResult game={selectedGame} userId={userId} />
        ) : (
          <BondWheelStagePlayers game={selectedGame} userId={userId} />
        )}

        {showJoinAction ? (
          <button
            type="button"
            className="bplay-wheel-action"
            disabled={actionLoading && busyGameId === selectedGame._id}
            onClick={handleJoinAction}
          >
            {actionLoading && busyGameId === selectedGame._id
              ? "…"
              : getUserTicketCount(selectedGame, userId) > 0
                ? `Add ticket · ${formatCoin(selectedGame.entryAmount)} COIN`
                : `Join · ${formatCoin(selectedGame.entryAmount)} COIN`}
          </button>
        ) : null}
      </div>
    );
  };

  const handleDrawToggleNumbers = (next) => {
    if (isDrawPlaying || drawActionLoading || drawPhase !== "idle") return;
    if (drawPendingRound) {
      setToast("Cancel your reserved ticket before changing picks.");
      return;
    }
    setDrawStatusMessage(null);
    if (!Array.isArray(next)) return;
    setDrawSelectedNumbers(next.slice(0, MAX_SELECTION));
  };

  const handleDrawBuy = () => {
    if (!isAuthenticated) {
      navigate("/app/login");
      return;
    }
    if (drawSelectedNumbers.length === 0) {
      setToast("Pick at least one number first.");
      return;
    }

    const quote = buildTicketQuote(drawSelectedNumbers.length);
    const coinBalance = Math.floor(Number(user?.COIN || 0));
    if (quote.stake > coinBalance) {
      setToast(`Need ${formatDrawCoin(quote.stake - coinBalance)} more COIN to reserve.`);
      return;
    }

    const poolActive = Number(drawPool?.activeTotal || 0);
    if (quote.reserve > 0 && poolActive < quote.reserve) {
      setToast("Investor pool is too low for this ticket. Try fewer picks.");
      return;
    }

    setDrawReserveQuote(quote);
    setDrawConfirm("reserve-draw");
  };

  const handleConfirmDrawReserve = async () => {
    if (drawSelectedNumbers.length === 0) return;
    try {
      const response = await dispatch(bondDrawBuyRound({ selectedNumbers: drawSelectedNumbers })).unwrap();
      const serverRound = response?.round || response;
      const serverMessage =
        response?.message ||
        response?.successMessage ||
        "Ticket reserved. Tap Draw now to play.";

      if (serverRound?.selectedNumbers?.length) {
        setDrawSelectedNumbers(serverRound.selectedNumbers);
      }
      setDrawConfirm(null);
      setDrawReserveQuote(null);
      dispatch(fetchBondDrawPool());
      dispatch(fetchBondDrawLanding());
      setDrawStatusMessage({ tone: "success", text: serverMessage });
      setToast(serverMessage);
    } catch (error) {
      const message = typeof error === "string" ? error : error?.message || "Could not reserve ticket.";
      setDrawStatusMessage({ tone: "error", text: message });
      setToast(message);
    }
  };

  const handleDrawPendingExpired = useCallback(() => {
    dispatch(clearPendingRound());
    dispatch(fetchBondDrawLanding());
    dispatch(fetchBondDrawPool());
    setDrawSelectedNumbers([]);
    setDrawConfirm(null);
    setDrawStatusMessage({ tone: "info", text: "Reservation expired. Stake refunded." });
    setToast("Reservation expired. Stake refunded.");
  }, [dispatch]);

  useEffect(() => {
    drawExpiredHandledRef.current = null;
  }, [drawPendingRound?._id]);

  useEffect(() => {
    if (!drawPendingRound?._id || !pendingExpiry.isExpired) return;
    if (drawExpiredHandledRef.current === drawPendingRound._id) return;
    drawExpiredHandledRef.current = drawPendingRound._id;
    handleDrawPendingExpired();
  }, [drawPendingRound, pendingExpiry.isExpired, handleDrawPendingExpired]);

  const handleDrawPlay = async () => {
    if (!drawPendingRound?._id || pendingExpiry.isExpired) return;
    try {
      setDrawStatusMessage(null);
      await playDrawRound(drawPendingRound);
    } catch (error) {
      setToast(typeof error === "string" ? error : error?.message || "Could not draw.");
    }
  };

  const handleDrawCancel = () => {
    if (!drawPendingRound?._id || pendingExpiry.isExpired) return;
    setDrawConfirm("cancel-draw");
  };

  const handleConfirmDrawCancel = async () => {
    if (!drawPendingRound?._id || pendingExpiry.isExpired) return;
    try {
      await cancelDrawRound(drawPendingRound);
      setDrawSelectedNumbers([]);
      setDrawConfirm(null);
      setDrawStatusMessage({ tone: "info", text: "Ticket cancelled. Stake refunded." });
      setToast("Ticket cancelled. Stake refunded.");
    } catch (error) {
      setToast(typeof error === "string" ? error : error?.message || "Could not cancel ticket.");
    }
  };

  const handleCloseDrawResult = () => {
    closeDrawResultModal();
    if (isAuthenticated && userId) dispatch(fetchBondDrawLanding());
  };

  const handleDrawInvestOpen = () => {
    if (!isAuthenticated) {
      navigate("/app/login");
      return;
    }
    setDrawInvestOpen(true);
  };

  const handleDrawInvestSubmit = async (amount) => {
    try {
      await dispatch(bondDrawInvest({ amount })).unwrap();
      setDrawInvestOpen(false);
      const message = `Added ${formatDrawCoin(amount)} COIN to the investor pool.`;
      setDrawStatusMessage({ tone: "success", text: message });
      setToast(message);
      dispatch(fetchBondDrawPool());
      dispatch(fetchBondDrawInvestors({ limit: 100 }));
    } catch (error) {
      const message = typeof error === "string" ? error : error?.message || "Invest failed.";
      setToast(message);
    }
  };

  const renderDrawCenter = () => {
    if (!isAuthenticated) {
      return (
        <div className="bdraw-empty">
          <Lock size={28} strokeWidth={1.5} />
          <p>Sign in to play Bond Draw.</p>
          <button type="button" className="bdraw-empty-btn" onClick={() => navigate("/app/login")}>
            Sign in
          </button>
        </div>
      );
    }

    if (drawLoading && !drawPendingRound) {
      return (
        <div className="bplay-wheel-loading">
          <Loader2 size={28} className="bplay-spin" />
        </div>
      );
    }

    const hasPending = !!drawPendingRound && !pendingExpiry.isExpired;
    const selectedForView = hasPending
      ? drawPendingRound.selectedNumbers || []
      : drawSelectedNumbers;
    const ticket = hasPending
      ? buildTicketQuoteFromRound(drawPendingRound)
      : buildTicketQuote(drawSelectedNumbers.length);
    const playLocked = isDrawPlaying || drawActionLoading || drawPhase !== "idle";
    const isDrawAnimating = drawPhase !== "idle";
    const coinBalance = Math.floor(Number(user?.COIN || 0));
    const poolActive = Number(drawPool?.activeTotal || 0);
    const canReserve =
      drawSelectedNumbers.length > 0 &&
      ticket.stake <= coinBalance &&
      (ticket.reserve <= 0 || poolActive >= ticket.reserve);

    const showDrawTicket = selectedForView.length > 0 || hasPending;

    const hitPct = Math.round((ticket?.hitProbability || 0) * 100);

    return (
      <div className="bdraw-stage-shell">
        <BondDrawNumberGrid
          selected={selectedForView}
          drawn={drawnNumbers}
          revealedDrawnCount={revealedDrawnCount}
          disabled={playLocked || hasPending}
          isDrawing={isDrawAnimating}
          drawPhase={drawPhase}
          drawnTotal={drawnNumbers.length || undefined}
          maxSelection={MAX_SELECTION}
          onToggleNumber={handleDrawToggleNumbers}
        />

        {!isDrawAnimating ? (
          <div className={`bdraw-stage-top${showDrawTicket ? " has-ticket" : ""}`}>
            <BondDrawStageHeader
              picks={selectedForView.length}
              maxSelection={MAX_SELECTION}
              hasPending={hasPending}
              hitPct={showDrawTicket ? hitPct : 0}
              onOpenInfo={() => setDrawInfoOpen(true)}
            />
            {showDrawTicket ? (
              <BondDrawTicket
                embedded
                ticket={ticket}
                picks={selectedForView.length}
                selectedNumbers={selectedForView}
                locked={hasPending}
                userCoin={coinBalance}
                poolActive={poolActive}
              />
            ) : null}
          </div>
        ) : null}

        {hasPending && !isDrawAnimating ? (
          <BondDrawPendingExpiryHint
            deadline={pendingExpiry.deadline}
            secondsLeft={pendingExpiry.secondsLeft}
          />
        ) : null}

        {!isDrawAnimating ? (
          <BondDrawStageActions
            hasPending={hasPending}
            pendingExpired={pendingExpiry.isExpired}
            canPlay={canReserve}
            disabled={playLocked}
            loading={drawActionLoading}
            onCancel={handleDrawCancel}
            onPlay={handleDrawPlay}
            onBuy={handleDrawBuy}
          />
        ) : null}

        {drawStatusMessage ? (
          <div
            key={drawStatusMessage.text}
            className={`bdraw-status-message flash ${drawStatusMessage.tone}`}
            role="status"
            aria-live="polite"
          >
            {drawStatusMessage.text}
          </div>
        ) : null}

      </div>
    );
  };

  const renderCenterStage = () => {
    if (gameTypeId === "bond-spin") return renderSpinCenter();
    if (gameTypeId === "bond-wheel") return renderWheelCenter();
    if (gameTypeId === "bond-draw") return renderDrawCenter();
    return (
      <div className="bplay-soon-panel">
        <Lock size={28} strokeWidth={1.5} />
        <p className="bplay-soon-title">Not on web yet</p>
        <p className="bplay-soon-copy">
          This Bond Play game is available in the BetBond Android app. Wheel, Spin, and Draw work here.
        </p>
        <a className="bplay-soon-link" href="/download">
          Get the Android app
        </a>
      </div>
    );
  };

  return (
    <div
      className="bplay-page"
      style={{
        "--bplay-accent": gameType.accent,
        "--bplay-accent-bg": gameType.accentBg,
        "--bplay-accent-border": gameType.accentBorder,
        "--bplay-accent-glow": gameType.accentGlow,
      }}
    >
      <div className="bplay-bg-glow" aria-hidden />

      <div className={`bplay-layout${gameTypeId === "bond-draw" ? " bplay-layout-draw" : ""}`}>
        <BondPlayGameTypeRail activeGameId={gameTypeId} onSelectInline={setGameTypeId} />

        <main
          className={`bplay-wheel-stage${
            gameTypeId === "bond-spin" ? " bplay-wheel-stage-spin" : ""
          }${gameTypeId === "bond-wheel" ? " bplay-wheel-stage-wheel" : ""}${
            gameTypeId === "bond-draw" ? " bplay-wheel-stage-draw" : ""
          }`}
        >
          {renderCenterStage()}
        </main>

        {gameTypeId === "bond-wheel" ? (
          <BondWheelGameRail
            games={railGames}
            selectedGameId={selectedGameId}
            onSelectGame={handleSelectGame}
            onLeave={(game) => {
              if (!isAuthenticated) {
                navigate("/app/login");
                return;
              }
              setLeaveConfirmGame(game);
            }}
            onCreate={() => (isAuthenticated ? setCreateOpen(true) : navigate("/app/login"))}
            loading={loading}
            leavingGameId={leavingGameId}
            userId={userId}
          />
        ) : gameTypeId === "bond-spin" ? (
          <BondSpinGameRail
            games={spinRailGames}
            selectedGameId={selectedSpinGameId}
            pendingBetGameIds={pendingBetGameIds}
            onSelectGame={handleSelectSpinGame}
            onCreate={openCreateSpin}
            loading={spinLoading}
            userId={userId}
          />
        ) : gameTypeId === "bond-draw" ? (
          <BondDrawSidePanel
            onAddBalance={isAuthenticated ? handleDrawInvestOpen : undefined}
            onToast={setToast}
          />
        ) : (
          <aside className="bplay-games-rail bplay-games-rail-muted" aria-hidden>
            <div className="bplay-games-rail-head">
              <span className="bplay-games-rail-label">Wheels</span>
            </div>
            <p className="bplay-games-empty">Not available yet.</p>
          </aside>
        )}
      </div>

      {toast ? <div className="bplay-toast">{toast}</div> : null}

      <CreateBondWheelModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        constants={constants}
        userCoinBalance={userCoin}
        submitting={actionLoading}
      />

      <CreateBondSpinModal
        open={createSpinOpen}
        onClose={() => setCreateSpinOpen(false)}
        onSubmit={handleCreateSpin}
        userCoinBalance={userCoin}
        submitting={spinActionLoading}
      />

      <PlaceBondSpinBetModal
        open={placeBetOpen}
        onClose={() => setPlaceBetOpen(false)}
        game={selectedSpinGame}
        userCoinBalance={userCoin}
        submitting={spinActionLoading}
        onSubmit={handlePlaceBet}
      />

      <BondWheelJoinModal
        open={!!joinConfirmGame}
        game={joinConfirmGame}
        userId={userId}
        userCoinBalance={userCoin}
        maxTicketsPerJoin={constants?.maxTicketsPerJoin}
        loading={!!joinConfirmGame && busyGameId === joinConfirmGame._id}
        onClose={() => !busyGameId && setJoinConfirmGame(null)}
        onConfirm={(ticketCount) => executeJoin(joinConfirmGame, ticketCount)}
      />

      <BondWheelLeaveModal
        open={!!leaveConfirmGame}
        game={leaveConfirmGame}
        loading={!!leaveConfirmGame && leavingGameId === leaveConfirmGame._id}
        onClose={() => !leavingGameId && setLeaveConfirmGame(null)}
        onConfirm={() => executeLeave(leaveConfirmGame)}
      />

      {spinConfirmModal ? (
        <BondPlayConfirmModal
          open
          eyebrow={spinConfirmModal.eyebrow}
          title={spinConfirmModal.title}
          description={spinConfirmModal.description}
          confirmLabel={spinConfirmModal.confirmLabel}
          cancelLabel={spinConfirmModal.cancelLabel}
          tone={spinConfirmModal.tone}
          loading={spinActionLoading}
          loadingLabel={spinConfirmModal.loadingLabel}
          onClose={() => !spinActionLoading && setSpinConfirm(null)}
          onConfirm={handleSpinConfirm}
        />
      ) : null}

      <BondSpinResultModal
        open={!!spinResultModal}
        message={spinResultModal?.message}
        tone={spinResultModal?.tone}
        bet={spinResultModal?.bet}
        gameResult={spinResultModal?.gameResult}
        winAmount={spinResultModal?.winAmount}
        betAmount={spinResultModal?.betAmount}
        onClose={handleCloseSpinResult}
      />

      <BondPlayConfirmModal
        open={drawConfirm === "reserve-draw"}
        eyebrow="Reserve ticket"
        title="Reserve your Bond Draw ticket?"
        tone="primary"
        confirmLabel="Reserve ticket"
        cancelLabel="Keep editing"
        loading={drawActionLoading}
        loadingLabel="Reserving…"
        description={
          drawReserveQuote ? (
            <>
              <p>
                Stake <strong>{formatDrawCoin(drawReserveQuote.stake)} COIN</strong> will be deducted
                from your wallet.
              </p>
              <p>
                Pool hold <strong>{formatDrawCoin(drawReserveQuote.reserve)} COIN</strong> covers your
                max win until you draw or cancel.
              </p>
              <p className="bdraw-confirm-numbers">
                {[...drawSelectedNumbers].sort((a, b) => a - b).join(" · ")}
              </p>
              <p>You have 90 seconds to tap Draw now or your stake is refunded.</p>
            </>
          ) : (
            <p>Confirm to lock your picks and reserve pool coverage.</p>
          )
        }
        onClose={() => !drawActionLoading && setDrawConfirm(null)}
        onConfirm={handleConfirmDrawReserve}
      />

      <BondPlayConfirmModal
        open={drawConfirm === "cancel-draw"}
        eyebrow="Cancel ticket"
        title="Cancel your Bond Draw ticket?"
        tone="danger"
        confirmLabel="Cancel ticket"
        cancelLabel="Keep ticket"
        loading={drawActionLoading}
        loadingLabel="Cancelling…"
        description={
          <p>
            Your <strong>{drawPendingRound?.stake || 0} COIN</strong> stake will be refunded immediately.
          </p>
        }
        onClose={() => !drawActionLoading && setDrawConfirm(null)}
        onConfirm={handleConfirmDrawCancel}
      />

      <BondDrawResultModal
        open={!!drawResultModal}
        isWin={!!drawResultModal?.isWin}
        matches={drawResultModal?.matches || 0}
        stake={drawResultModal?.stake || 0}
        winAmount={drawResultModal?.winAmount || 0}
        netWinAmount={drawResultModal?.netWinAmount ?? null}
        selectedNumbers={drawResultModal?.selectedNumbers || []}
        drawnNumbers={drawResultModal?.drawnNumbers || []}
        matchedNumbers={drawResultModal?.matchedNumbers || []}
        onClose={handleCloseDrawResult}
      />

      <BondDrawInvestModal
        open={drawInvestOpen}
        maxAmount={Math.floor(Number(user?.COIN || 0))}
        loading={drawInvestorActionLoading}
        isExistingInvestor={isDrawInvestor}
        onClose={() => !drawInvestorActionLoading && setDrawInvestOpen(false)}
        onSubmit={handleDrawInvestSubmit}
      />

      <BondDrawInfoModal open={drawInfoOpen} onClose={() => setDrawInfoOpen(false)} />

      <BondSpinInfoModal open={spinInfoOpen} onClose={() => setSpinInfoOpen(false)} />

      <BondWheelInfoModal open={wheelInfoOpen} onClose={() => setWheelInfoOpen(false)} constants={constants} />
    </div>
  );
};

export default BondPlay;
